import { createTranslator, readI18n } from './i18n.js';
import { fixKanbanSectionColors } from './mermaid-svg.js';

// mermaid はサイズが大きいため main.js/public.js には同梱せず、
// ```mermaid ブロックが実際に表示されるときだけ読み込む。
//
// 読み込み元は自ドメインの assets/vendor/mermaid.esm.js（`just build` が生成）。
// 以前は CDN から読んでいたが、編集セッショントークンは <meta> にあり JS から
// 読めるため、CDN 側が汚染されるとアカウントを乗っ取られる経路になっていた。
// 第三者オリジンを一切使わないことで、この経路をなくしている。
//
// import() の引数を変数にしているのは、esbuild がバンドル時にこのファイルを
// main.js へ取り込まないようにするため（意図的に静的解析を外す）。
// document.baseURI から絶対URLを作るのは、サブディレクトリ設置
// （例 https://uta.pw/outline_test/）でも正しく解決させるため。
const MERMAID_MODULE_PATH = 'assets/vendor/mermaid.esm.js';

// 画面に出すエラーメッセージは、ページに埋め込まれた翻訳（#app-i18n）から読む。
// 編集画面・公開画面のどちらでも使えるよう、呼び出し側から翻訳関数を受け取らず、必要になったときに読む。
function translate(key: string) {
  return createTranslator(readI18n())(key);
}

let mermaidModulePromise = null;

let renderCounter = 0;

function loadMermaid() {
  if (!mermaidModulePromise) {
    // Flutter の同梱ビューア（app/assets/mermaid/mindmap.html）では、WKWebView の
    // file:// で ES モジュールの動的 import が使えないため、IIFE バンドル
    // （app/assets/mermaid/mermaid.js, グローバル名 MermaidBundle）を使う。
    // Web では未設定なので従来どおり動的 import になる。
    const bundled = window.MermaidBundle;
    if (bundled) {
      mermaidModulePromise = Promise.resolve(bundled);
    } else {
      const url = new URL(MERMAID_MODULE_PATH, document.baseURI).href;
      mermaidModulePromise = import(url);
    }
  }
  return mermaidModulePromise;
}

function currentMermaidTheme() {
  const scheme = document.body.getAttribute('data-color-scheme') || '';
  return scheme.includes('dark') ? 'dark' : 'default';
}

async function initializedMermaid() {
  const mod = await loadMermaid();
  const mermaid = mod.default;
  mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: currentMermaidTheme() });
  return mermaid;
}

// 任意の mermaid ソースを描画して SVG 文字列を返す。マインドマップ表示のように、
// ノード本文ではなくアプリ側が組み立てた図を出すときに使う。
export async function renderMermaidSource(source) {
  const mermaid = await initializedMermaid();
  const { svg } = await mermaid.render(`mermaid-diagram-${renderCounter++}`, String(source || ''));
  return fixKanbanSectionColors(svg);
}

// テーマ変更の追随は行わず、描画時点のテーマで固定する。
// ノード編集で再描画されるたびに formatNodeMarkdown() が新しい .md-mermaid を生成するため、
// 通常の利用では次の描画で新しいテーマが反映される。
export async function renderMermaidBlocks(container: ParentNode | null | undefined) {
  if (!container) return;
  const pending = container.querySelectorAll<HTMLElement>('.md-mermaid[data-mermaid-pending="1"]');
  if (pending.length === 0) {
    return;
  }

  let mermaid;
  try {
    mermaid = await initializedMermaid();
  } catch (error) {
    console.error('mermaidの読み込みに失敗しました', error);
    pending.forEach((el) => {
      el.removeAttribute('data-mermaid-pending');
      el.classList.add('is-error');
      el.textContent = translate('mermaid_load_error');
    });
    return;
  }

  for (const el of Array.from(pending)) {
    el.removeAttribute('data-mermaid-pending');
    const encoded = el.getAttribute('data-mermaid-source') || '';
    let source = '';
    try {
      source = decodeURIComponent(encoded);
    } catch (error) {
      source = '';
    }
    const renderId = `mermaid-diagram-${renderCounter++}`;
    try {
      const { svg } = await mermaid.render(renderId, source);
      el.innerHTML = fixKanbanSectionColors(svg);
      el.classList.add('is-rendered');
      el.removeAttribute('data-mermaid-source');
    } catch (error) {
      el.classList.add('is-error');
      el.textContent = translate('mermaid_syntax_error');
    }
  }
}
